import { API_BASE_URL } from '@/constants/api';

interface PhraseApiConfig {
  branch: string;
  baseUrl: string;
  projectId: string;
  accessToken: string;
}

interface AppConfig {
  api: { baseUrl: string };
  auth: { tokenRefreshBufferMinutes: number; requestTimeoutMs: number; enableApiLogging: boolean };
  phrase: {
    host: string;
    distribution: string;
    secret: string;
    appVersion: string;
    cacheExpirationTimeMs: number;
    /**
     *  Use it to use the Phrase API to fetch Strings for specific branches before being released (Only for local Development).
     */
    api: PhraseApiConfig | null;
  };
  useMswMocks: boolean;
  /** TODO(HUM-25935): temporary — mock /metrics and /get-started until the backend supports them again. */
  useTemporaryEndpointsMock: boolean;
  newRelic: { enableOnLocal: boolean; baseUrl: string };
  logger: { forceRemote: boolean };
  isDevelopment: boolean;
}

const DEFAULT_PHRASE_CACHE_EXPIRATION_TIME_MS = 30 * 60 * 1000; // 30 minutes
const DEFAULT_OTA_BASE_URL = 'https://ota.eu.phrase.com';
const DEFAULT_API_BASE_URL = 'https://api.phrase.com/v2/projects';

/**
 * Parse value based on fallback type
 */
function parseValue<T>(value: string, fallback: T): T {
  if (typeof fallback === 'number') {
    // Empty string should use fallback for numbers
    if (value.trim() === '') {
      return fallback;
    }
    const parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : (parsed as T);
  }
  if (typeof fallback === 'boolean') {
    return (value === 'true') as T;
  }
  // For strings, empty string is a valid value
  return value as T;
}

/**
 * Get environment variable with fallback for test environment
 * Supports string, number, and boolean types
 */
function getEnvVar<T>(key: string, fallback: T): T {
  // Try process.env first (works in Node.js test environment)
  if (typeof process !== 'undefined' && process.env?.[key]) {
    return parseValue(process.env[key]!, fallback);
  }

  // Try import.meta.env (works in browser/Vite environment)
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env?.[key]) {
      return parseValue(import.meta.env[key], fallback);
    }
  } catch {
    // Fallback when import.meta is not available
  }

  return fallback;
}

const loadPhraseApiConfig = (): PhraseApiConfig | null => {
  const isLocal = getEnvVar<'production' | 'development'>('MODE', 'production') === 'development';
  if (!isLocal) {
    return null;
  }

  const isPhraseApiEnabled = getEnvVar('VITE_PHRASE_API_ENABLED', false);
  if (!isPhraseApiEnabled) {
    return null;
  }

  const baseUrl = getEnvVar('VITE_PHRASE_API_BASE_URL', DEFAULT_API_BASE_URL);
  const projectId = getEnvVar('VITE_PHRASE_API_PROJECT_ID', '');
  const accessToken = getEnvVar('VITE_PHRASE_API_ACCESS_TOKEN', '');
  const branch = getEnvVar('VITE_PHRASE_API_BRANCH', 'main');

  return { baseUrl, projectId, accessToken, branch };
};

export const appConfig: AppConfig = {
  api: { baseUrl: getEnvVar('VITE_API_BASE_URL', API_BASE_URL) },
  auth: {
    tokenRefreshBufferMinutes: getEnvVar('VITE_TOKEN_REFRESH_BUFFER_MINUTES', 5),
    requestTimeoutMs: getEnvVar('VITE_REQUEST_TIMEOUT_MS', 10_000),
    enableApiLogging: getEnvVar('VITE_ENABLE_API_LOGGING', false),
  },
  phrase: {
    host: getEnvVar('VITE_PHRASE_HOST', DEFAULT_OTA_BASE_URL),
    distribution: getEnvVar('VITE_PHRASE_DISTRIBUTION', ''),
    secret: getEnvVar('VITE_PHRASE_SECRET', ''),
    appVersion: getEnvVar('VITE_PHRASE_APP_VERSION', '1.0.0'),
    cacheExpirationTimeMs: getEnvVar(
      'VITE_PHRASE_CACHE_EXPIRATION_TIME_MS',
      DEFAULT_PHRASE_CACHE_EXPIRATION_TIME_MS
    ),
    api: loadPhraseApiConfig(),
  },
  /** Activate it only to use mock server */
  useMswMocks: getEnvVar('VITE_USE_MSW_MOCKS', false),
  useTemporaryEndpointsMock: getEnvVar('VITE_TEMPORARY_ENDPOINTS_MOCK', false),
  newRelic: {
    enableOnLocal: getEnvVar('VITE_NEW_RELIC_ENABLE_ON_LOCAL', false),
    baseUrl: 'https://*.nr-data.net',
  },
  logger: { forceRemote: getEnvVar('VITE_LOGGER_FORCE_REMOTE', false) },
  isDevelopment: getEnvVar<string>('NODE_ENV', 'production') === 'development',
};
